import { useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

type BookingOpening = {
  // Matches the exact hostname and all its subdomains (e.g. 'example.com' matches 'www.example.com')
  domain: string;
  // ISO 8601 date with explicit offset, e.g. '2026-10-01T10:00:00+02:00'
  opensAt: string;
  // Optional date shown in the message when it must differ from the real opening date (same format as opensAt)
  displayedOpensAt?: string;
  // Time zone used to display the opening date and time to the visitor
  timeZone: string;
};

const BOOKING_OPENINGS: BookingOpening[] = [
  {
    domain: 'semaines-tourisme-economique.bzh',
    opensAt: '2026-09-30T09:00:00+02:00',
    displayedOpensAt: '2026-10-01T09:00:00+02:00',
    timeZone: 'Europe/Paris',
  },
  {
    domain: 'localhost',
    opensAt: '2026-09-29T17:00:00+02:00',
    timeZone: 'Europe/Paris',
  },
];

const NOW_REFRESH_INTERVAL_MS = 30_000;

// Client-side preview only (not a security measure): set to 'true' in the browser console to see the booking widget before opening
const BOOKING_OPENING_BYPASS_STORAGE_KEY = 'yb-booking-opening-bypass';

function isBookingOpeningBypassed(): boolean {
  try {
    return localStorage.getItem(BOOKING_OPENING_BYPASS_STORAGE_KEY) === 'true';
  }
  catch {
    return false;
  }
}

function isHostnameMatchingDomain(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function findBookingOpeningForHostname(hostname: string): BookingOpening | undefined {
  return BOOKING_OPENINGS.find(bookingOpening => isHostnameMatchingDomain(hostname, bookingOpening.domain));
}

export default function useBookingOpening() {
  const { currentLocale } = storeToRefs(useGlobalConfigurationStore());
  const now = useNow({ interval: NOW_REFRESH_INTERVAL_MS });

  const bookingOpening = findBookingOpeningForHostname(window.location.hostname);
  const openingDate = bookingOpening ? new Date(bookingOpening.opensAt) : null;
  const displayedOpeningDate = bookingOpening ? new Date(bookingOpening.displayedOpensAt ?? bookingOpening.opensAt) : null;
  const hasBypass = isBookingOpeningBypassed();

  const isBookingOpened = computed(() => hasBypass || openingDate === null || now.value >= openingDate);

  const formattedOpeningDate = computed(() => {
    if (!bookingOpening || !displayedOpeningDate)
      return '';
    return displayedOpeningDate.toLocaleDateString(currentLocale.value, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: bookingOpening.timeZone,
    });
  });

  const formattedOpeningTime = computed(() => {
    if (!bookingOpening || !displayedOpeningDate)
      return '';
    return displayedOpeningDate.toLocaleTimeString(currentLocale.value, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: bookingOpening.timeZone,
    });
  });

  return {
    isBookingOpened,
    formattedOpeningDate,
    formattedOpeningTime,
  };
}
